/**
 * PillarPage — reusable SEO/marketing pillar-page template (iter 172).
 *
 * Renders a category-owning landing page from a single config object.
 * Every pillar page produced with this component ships with:
 *   - Seo helmet (title, meta description, canonical, keywords, OG, Twitter)
 *   - JSON-LD: Organization + WebSite + BreadcrumbList + SoftwareApplication
 *     + optional FAQPage
 *   - PublicNavbar + Footer for site consistency
 *   - Hero, benefits grid, workflow steps, FAQ, and dual CTAs
 *
 * Reuses the site design system — same brand colors, typography, and
 * component vocabulary as DischargePlannersPage. No new brand tokens.
 *
 * `config.slug` MUST match the route path (without leading /). The
 * server-side `SITEMAP_STATIC_PATHS` in server.py must include the same
 * slug or the page will not be discoverable.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight, ShieldCheck, Sparkles, ChevronDown,
} from 'lucide-react';
import { PublicNavbar } from '../brand/PublicNavbar';
import { Footer } from '../brand/Footer';
import { Seo } from '../seo/Seo';
import {
  organizationSchema, websiteSchema, breadcrumbSchema,
  softwareApplicationSchema, faqSchema,
} from '../../lib/seoSchemas';

function FaqRow({ q, a, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="rounded-2xl border border-cn-ink/10 bg-white/60 backdrop-blur-sm overflow-hidden"
      data-testid={testId}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-4 px-5 py-4 text-left"
        data-testid={`${testId}-toggle`}
      >
        <span className="text-cn-ink font-semibold leading-snug">{q}</span>
        <ChevronDown className={`h-4 w-4 mt-1 text-cn-ink/60 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 -mt-1 text-cn-ink/70 leading-relaxed">{a}</div>
      )}
    </div>
  );
}

export function PillarPage({ config }) {
  const {
    slug, title, metaTitle, metaDescription, keywords = [],
    h1, subhead, heroSupport, primaryCta = { label: 'Request a demo', href: '/contact?type=demo' },
    secondaryCta = { label: 'For discharge planners', href: '/for-discharge-planners' },
    benefits = [],           // [{icon, k, title, body}]
    workflow = [],           // [{n, title, body}]
    audiencePaths = [],      // [{title, body, cta, href}]
    faq = [],                // [{q, a}]
    softwareFeatures = [],
    breadcrumbTrail,         // optional custom trail; default = [Home > title]
  } = config;

  const path = `/${slug}`;
  const trail = breadcrumbTrail || [
    { name: 'Home', path: '/' },
    { name: title || h1, path },
  ];
  const schemas = [
    organizationSchema(),
    websiteSchema(),
    breadcrumbSchema(trail),
    softwareApplicationSchema({
      description: metaDescription,
      slug,
      featureList: softwareFeatures,
    }),
  ];
  if (faq.length) schemas.push(faqSchema(faq));

  return (
    <div className="min-h-screen bg-cn-cream">
      <Seo
        title={metaTitle}
        description={metaDescription}
        path={path}
        keywords={keywords}
        schema={schemas}
      />
      <PublicNavbar />

      {/* Hero */}
      <section
        className="relative overflow-hidden pt-16 pb-20 md:pt-24 md:pb-28"
        data-testid={`pillar-${slug}-hero`}
      >
        <div className="absolute inset-0 pointer-events-none opacity-70">
          <div className="absolute -top-40 -left-40 h-96 w-96 rounded-full bg-cn-primary/10 blur-3xl" />
          <div className="absolute -bottom-40 right-0 h-[520px] w-[520px] rounded-full bg-cn-primary/5 blur-3xl" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6">
          <nav aria-label="Breadcrumb" className="text-xs text-cn-ink/50 mb-6" data-testid="pillar-breadcrumb">
            <Link to="/" className="hover:text-cn-primary">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-cn-ink/70">{title || h1}</span>
          </nav>
          <div className="inline-flex items-center gap-1.5 rounded-full bg-cn-primary/10 border border-cn-primary/25 px-3 py-1 text-xs font-semibold text-cn-primary tracking-wide uppercase mb-5">
            <Sparkles className="h-3 w-3" /> {config.eyebrow || 'CaraLoom Platform'}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-cn-ink tracking-tight leading-[1.05]" data-testid="pillar-h1">
            {h1}
          </h1>
          <p className="mt-6 text-lg md:text-xl text-cn-ink/70 max-w-2xl leading-relaxed">
            {subhead}
          </p>
          {heroSupport && (
            <p className="mt-4 text-base text-cn-ink/60 max-w-2xl leading-relaxed">
              {heroSupport}
            </p>
          )}
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to={primaryCta.href}
              className="inline-flex items-center gap-1.5 bg-cn-primary text-white rounded-full px-6 py-3 font-semibold text-sm hover:bg-cn-primary/90 transition-colors"
              data-testid="pillar-primary-cta"
            >
              {primaryCta.label} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to={secondaryCta.href}
              className="inline-flex items-center gap-1.5 border border-cn-ink/20 text-cn-ink rounded-full px-6 py-3 font-semibold text-sm hover:border-cn-primary/60 transition-colors"
              data-testid="pillar-secondary-cta"
            >
              {secondaryCta.label} <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits grid */}
      {benefits.length > 0 && (
        <section className="py-16 md:py-20 border-t border-cn-ink/5" data-testid="pillar-benefits">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-semibold text-cn-ink mb-3 max-w-3xl">
              {config.benefitsHeading || 'What CaraLoom does for your team'}
            </h2>
            {config.benefitsSubhead && (
              <p className="text-cn-ink/60 max-w-2xl mb-10">{config.benefitsSubhead}</p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {benefits.map((b, i) => (
                <div
                  key={b.k || i}
                  className="rounded-2xl border border-cn-ink/10 bg-white/60 p-6 backdrop-blur-sm"
                  data-testid={`pillar-benefit-${b.k || i}`}
                >
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-cn-primary font-semibold mb-2">
                    {b.icon && <b.icon className="h-4 w-4" />}
                    {b.k}
                  </div>
                  <h3 className="text-lg font-semibold text-cn-ink leading-snug mb-2">{b.title}</h3>
                  <p className="text-cn-ink/70 leading-relaxed">{b.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Workflow steps */}
      {workflow.length > 0 && (
        <section className="py-16 md:py-20 bg-cn-primary/5 border-t border-cn-ink/5" data-testid="pillar-workflow">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-semibold text-cn-ink mb-3">
              {config.workflowHeading || 'How it works'}
            </h2>
            {config.workflowSubhead && (
              <p className="text-cn-ink/60 max-w-2xl mb-10">{config.workflowSubhead}</p>
            )}
            <ol className="space-y-4">
              {workflow.map((step) => (
                <li
                  key={step.n}
                  className="flex gap-5 rounded-2xl bg-white border border-cn-ink/10 p-5"
                  data-testid={`pillar-step-${step.n}`}
                >
                  <div className="flex-shrink-0 h-9 w-9 rounded-full bg-cn-primary text-white flex items-center justify-center font-semibold">
                    {step.n}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-cn-ink mb-1">{step.title}</h3>
                    <p className="text-cn-ink/70 leading-relaxed">{step.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}

      {/* Audience-specific paths */}
      {audiencePaths.length > 0 && (
        <section className="py-16 md:py-20 border-t border-cn-ink/5" data-testid="pillar-audiences">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-semibold text-cn-ink mb-10 max-w-3xl">
              {config.audienceHeading || 'Built for every seat in the discharge huddle'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {audiencePaths.map((a) => (
                <Link
                  key={a.href}
                  to={a.href}
                  className="rounded-2xl border border-cn-ink/10 bg-white/60 p-6 backdrop-blur-sm hover:border-cn-primary/40 hover:bg-cn-primary/5 transition-colors group"
                  data-testid={`pillar-audience-${a.href.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                >
                  <h3 className="text-lg font-semibold text-cn-ink mb-2 group-hover:text-cn-primary transition-colors">{a.title}</h3>
                  <p className="text-cn-ink/70 leading-relaxed mb-4">{a.body}</p>
                  <span className="text-sm font-semibold text-cn-primary inline-flex items-center gap-1">
                    {a.cta} <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {faq.length > 0 && (
        <section className="py-16 md:py-20 bg-cn-primary/5 border-t border-cn-ink/5" data-testid="pillar-faq">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-semibold text-cn-ink mb-3">
              {config.faqHeading || 'Frequently asked'}
            </h2>
            <p className="text-cn-ink/60 mb-10 max-w-2xl">
              {config.faqSubhead || 'Straight answers from CaraLoom\u2019s clinical + engineering teams.'}
            </p>
            <div className="space-y-3">
              {faq.map((row, i) => (
                <FaqRow key={i} q={row.q} a={row.a} testId={`pillar-faq-${i}`} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="py-16 md:py-20 border-t border-cn-ink/5" data-testid="pillar-bottom-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <ShieldCheck className="h-10 w-10 mx-auto text-cn-primary mb-4" />
          <h2 className="text-2xl md:text-3xl font-semibold text-cn-ink mb-4">
            {config.bottomCtaTitle || 'See CaraLoom for your health system'}
          </h2>
          <p className="text-cn-ink/70 max-w-2xl mx-auto mb-8">
            {config.bottomCtaBody || 'Walk through a real discharge — from referral to placement to tracked outcome — in under 30 minutes. No PHI required for the demo.'}
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link
              to={primaryCta.href}
              className="inline-flex items-center gap-1.5 bg-cn-primary text-white rounded-full px-6 py-3 font-semibold text-sm hover:bg-cn-primary/90 transition-colors"
              data-testid="pillar-bottom-primary-cta"
            >
              {primaryCta.label} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to={secondaryCta.href}
              className="inline-flex items-center gap-1.5 border border-cn-ink/20 text-cn-ink rounded-full px-6 py-3 font-semibold text-sm hover:border-cn-primary/60 transition-colors"
              data-testid="pillar-bottom-secondary-cta"
            >
              {secondaryCta.label} <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default PillarPage;
